import { usePortalAccess } from '../../components/PortalAccess';
import { useEffect, useState, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getDashboardKPI, getDashboardOverview, getTopPosts, getMoM } from '../../utils/smmApi'
import { KPICard } from '../../components/smm/KPICard'
import { PeriodSelector } from '../../components/smm/PeriodSelector'
import { formatNumber, formatFull, formatDate, erClass } from '../../utils/smmFormat'
import styles from './SmmDashboard.module.css'

const PERIOD_OPTIONS = [
  { label: '7д', value: 7 },
  { label: '30д', value: 30 },
  { label: 'Пред. месяц', value: 'prev_month' },
  { label: 'Квартал', value: 90 },
  { label: 'Даты', value: 'custom' },
]

function getPrevMonthRange() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const year = first.getFullYear()
  const month = first.getMonth() + 1
  const lastDate = new Date(year, month, 0).getDate()
  const pad = (n) => String(n).padStart(2, '0')
  return { date_from: `${year}-${pad(month)}-01`, date_to: `${year}-${pad(month)}-${pad(lastDate)}` }
}

function getApiParams(period, dateFrom, dateTo) {
  if (period === 'custom' && dateFrom && dateTo) return { date_from: dateFrom, date_to: dateTo }
  if (period === 'prev_month') return getPrevMonthRange()
  if (typeof period === 'number') return { period_days: period }
  return { period_days: 30 }
}

export default function SmmDashboard() {
  const { permissions } = usePortalAccess();

  const location = useLocation()
  const isAdminContext = location.pathname.startsWith('/admin/smm')
  const basePath = isAdminContext ? '/admin/smm' : '/smm'
  const [period, setPeriod] = useState(() => {
    try {
      const s = localStorage.getItem('global_period')
      if (!s) return 30
      const v = JSON.parse(s)
      return typeof v === 'number' || typeof v === 'string' ? v : 30
    } catch { return 30 }
  })
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [kpi, setKpi] = useState(null)
  const [overview, setOverview] = useState([])
  const [topPosts, setTopPosts] = useState([])
  const [mom, setMom] = useState([])
  const [loading, setLoading] = useState(true)
  const visibleOverview = overview.filter(p => permissions[`smm.${p.slug}`])
  const visibleMom = mom.filter(p => permissions[`smm.${p.slug}`])

  const loadData = useCallback(() => {
    if (period === 'custom' && (!dateFrom || !dateTo)) return
    const params = getApiParams(period, dateFrom, dateTo)
    setLoading(true)
    Promise.allSettled([getDashboardKPI(params), getDashboardOverview(params), getTopPosts(5), getMoM()])
      .then(([kRes, oRes, tRes, mRes]) => {
        if (kRes.status === 'fulfilled') setKpi(kRes.value)
        else console.error(kRes.reason)
        if (oRes.status === 'fulfilled') setOverview(oRes.value)
        else console.error(oRes.reason)
        if (tRes.status === 'fulfilled') setTopPosts(tRes.value)
        else console.error(tRes.reason)
        if (mRes.status === 'fulfilled') setMom(mRes.value)
        else console.error(mRes.reason)
      })
      .finally(() => setLoading(false))
  }, [period, dateFrom, dateTo])

  useEffect(() => { loadData() }, [loadData])

  if (loading && !kpi) {
    return <div className={styles.loadingCenter}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Главный дашборд</h1>
          <p className={styles.subtitle}>Сводная аналитика по всем платформам</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodSelector options={PERIOD_OPTIONS} value={period} onChange={v => {
            setPeriod(v)
            try { localStorage.setItem('global_period', JSON.stringify(v)) } catch {}
          }} />
          {period === 'custom' && (
            <>
              <input type="date" className="form-input" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} style={{ fontSize: 13, width: 140 }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
              <input type="date" className="form-input" value={dateTo}
                onChange={e => setDateTo(e.target.value)} style={{ fontSize: 13, width: 140 }} />
            </>
          )}
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <KPICard label="Всего подписчиков" value={kpi ? formatFull(kpi.total_subscribers) : null} tooltip="Суммарное количество подписчиков на всех активных платформах" source="Расчёт" />
        <KPICard label="Суммарный охват" value={kpi ? formatNumber(kpi.total_reach) : null} tooltip="Суммарный охват по всем активным платформам за выбранный период" source="Расчёт" />
        <KPICard label="Средний ER" value={kpi ? `${kpi.avg_er}%` : null} tooltip="Средний Engagement Rate по всем активным платформам" source="Расчёт" />
        <KPICard label="Активных платформ" value={kpi?.active_platforms ?? null} tooltip="Количество активно ведущихся платформ" source="Расчёт" />
      </div>

      {visibleOverview.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Платформы</h2>
          <div className={styles.platformGrid}>
            {visibleOverview.map(p => (
              <Link key={p.slug} to={`${basePath}/${p.slug}`} className={styles.platformCard}>
                <div className={styles.platformHeader}>
                  <span className={styles.platformDot} style={{ background: p.color }} />
                  <span className={styles.platformName}>{p.name}</span>
                </div>
                <div className={styles.platformStats}>
                  <div>
                    <div className={styles.statValue}>{p.subscribers !== null ? formatFull(p.subscribers) : '—'}</div>
                    <div className={styles.statLabel}>подписчики</div>
                  </div>
                  <div>
                    <div className={styles.statValue}><InlineValue value={p.er !== null ? `${p.er}%` : null} delta={p.delta?.er ?? null} /></div>
                    <div className={styles.statLabel}>ER</div>
                  </div>
                  <div>
                    <div className={styles.statValue}><InlineValue value={p.total_reach !== null ? formatNumber(p.total_reach) : null} delta={p.delta?.total_reach ?? null} /></div>
                    <div className={styles.statLabel}>охват</div>
                  </div>
                </div>
                {((p.total_likes !== null && p.total_likes > 0) || (p.total_comments !== null && p.total_comments > 0) || (p.total_reposts !== null && p.total_reposts > 0)) && (
                  <div className={styles.platformReactions}>
                    <ReactionStat label="лайки" value={p.total_likes} delta={p.delta?.total_likes ?? null} />
                    <ReactionStat label="комм." value={p.total_comments} delta={p.delta?.total_comments ?? null} />
                    <ReactionStat label="реп." value={p.total_reposts} delta={p.delta?.total_reposts ?? null} />
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {visibleMom.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Month-over-Month</h2>
          <div className={styles.momTable}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Платформа</th>
                  <th>Подписчики</th>
                  <th>Охват</th>
                  <th>ER</th>
                </tr>
              </thead>
              <tbody>
                {visibleMom.map(entry => (
                  <tr key={entry.slug}>
                    <td>
                      <span className={styles.platformDot} style={{ background: entry.color }} />
                      {entry.name}
                    </td>
                    <td><InlineValue value={entry.current?.subscribers != null ? formatFull(entry.current.subscribers) : null} delta={entry.deltas.subscribers} /></td>
                    <td><InlineValue value={entry.current?.total_reach != null ? formatNumber(entry.current.total_reach) : null} delta={entry.deltas.total_reach} /></td>
                    <td><InlineValue value={entry.current?.er != null ? `${entry.current.er}%` : null} delta={entry.deltas.er} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {topPosts.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Топ-5 постов по ER</h2>
          <div className={styles.topPostsTable}>
            <table className={styles.table}>
              <thead>
                <tr><th>Пост</th><th>Дата</th><th>Охват</th><th>Лайки</th><th>Комм.</th><th>Реп.</th><th>ER</th><th>ERR</th></tr>
              </thead>
              <tbody>
                {topPosts.map(p => (
                  <tr key={p.post_id}>
                    <td className={styles.postPreview}>
                      {p.post_url ? (
                        <a href={p.post_url} target="_blank" rel="noopener noreferrer" className={styles.postLink}>
                          {p.text_preview || '(без текста)'}
                        </a>
                      ) : (p.text_preview || '(без текста)')}
                    </td>
                    <td className={styles.dateCell}>{formatDate(p.published_at)}</td>
                    <td>{formatNumber(p.reach)}</td>
                    <td>{formatNumber(p.likes)}</td>
                    <td>{formatNumber(p.comments)}</td>
                    <td>{formatNumber(p.reposts)}</td>
                    <td className={erClass(p.er)}>{p.er}%</td>
                    <td className={erClass(p.err)}>{p.err}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {visibleOverview.length === 0 && !loading && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📊</div>
          <h3>Нет данных</h3>
          <p>{isAdminContext ? 'Внесите данные для Instagram или Макс, либо подключите API ВКонтакте и Telegram в разделе Настройки.' : 'Подключите API ВКонтакте и Telegram, чтобы здесь появилась аналитика.'}</p>
        </div>
      )}
    </div>
  )
}

function InlineValue({ value, delta }) {
  if (value === null || value === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      <span>{value}</span>
      {delta !== null && delta !== undefined && (
        <span style={{ fontSize: 11, fontWeight: 600, color: delta > 0 ? 'var(--success)' : delta < 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
          {delta > 0 ? '↑' : delta < 0 ? '↓' : '='}{Math.abs(delta)}%
        </span>
      )}
    </span>
  )
}

function ReactionStat({ label, value, delta }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
        {value !== null ? formatNumber(value) : '—'}
        {delta !== null && (
          <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 2, color: delta > 0 ? 'var(--success)' : delta < 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
            {delta > 0 ? '↑' : delta < 0 ? '↓' : '='}{Math.abs(delta)}%
          </span>
        )}
      </span>
      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</span>
    </span>
  )
}
